"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Calendar, User, Tag, ChevronLeft } from "lucide-react";

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="w-full max-w-4xl mx-auto animate-pulse">
        <div className="h-8 w-32 bg-muted rounded-md mb-8"></div>
        <div className="h-12 w-3/4 bg-muted rounded-md mb-4 mx-auto"></div>
        <div className="h-4 w-1/2 bg-muted rounded-md mb-12 mx-auto"></div>
        <div className="w-full h-[400px] bg-muted rounded-xl mb-12"></div>
        <div className="space-y-4">
          <div className="h-4 w-full bg-muted rounded-md"></div>
          <div className="h-4 w-full bg-muted rounded-md"></div>
          <div className="h-4 w-5/6 bg-muted rounded-md"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-destructive mb-4">Failed to load club news.</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold font-default mb-4 text-foreground">Article Not Found</h2>
        <p className="text-muted-foreground mb-8">The match report or club update you are looking for is unavailable.</p>
        <Link to="/HomePage">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none">Return to Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  
  const formattedDate = post.date ? new Date(post.date).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : null;

  return (
    <article className="w-full max-w-4xl mx-auto">
      <div className="mb-8">
        <Link to="/HomePage" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary transition-colors">
          <ChevronLeft className="size-4 mr-1" />
          Back to Club News
        </Link>
      </div>

      <header className="mb-10 text-center">
        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {post.categories.map((category, i) => (
              <Link key={category.id} to={category.link || "#"} data-index={i}>
                <span className="inline-flex items-center px-3 py-1 rounded-none bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider hover:bg-primary/80 transition-colors">
                  {category.name}
                </span>
              </Link>
            ))}
          </div>
        )}

        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default leading-tight mb-6 text-foreground uppercase tracking-tight">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
          {post.author && (
            <div className="flex items-center gap-2">
              {post.author.avatar && post.author.avatar['24'] ? (
                <img src={post.author.avatar['24']} alt={post.author.name} className="size-6 rounded-full border border-border" />
              ) : (
                <User className="size-4" />
              )}
              <span className="font-semibold text-foreground uppercase">{post.author.name}</span>
            </div>
          )}
          
          {formattedDate && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time dateTime={post.date} className="font-medium">{formattedDate}</time>
            </div>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <figure className="mb-12 rounded-none overflow-hidden border-4 border-primary shadow-2xl">
          <img 
            src={post.featuredImage} 
            alt={post.title} 
            className="w-full h-auto max-h-[600px] object-cover"
            loading="eager"
          />
        </figure>
      )}

      <div 
        className="prose prose-invert prose-lg max-w-none mb-16 text-muted-foreground font-default
          [&>p]:mb-6 [&>p]:leading-relaxed
          [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:font-default [&>h2]:text-foreground [&>h2]:mt-12 [&>h2]:mb-6 [&>h2]:uppercase [&>h2]:tracking-tight
          [&>h3]:text-2xl [&>h3]:font-bold [&>h3]:font-default [&>h3]:text-foreground [&>h3]:mt-8 [&>h3]:mb-4 [&>h3]:uppercase
          [&>h4]:text-xl [&>h4]:font-bold [&>h4]:font-default [&>h4]:text-foreground [&>h4]:mt-6 [&>h4]:mb-4
          [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul>li]:mb-2
          [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>ol>li]:mb-2
          [&>a]:text-primary [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-primary/80
          [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-foreground/90 [&>blockquote]:bg-muted/30 [&>blockquote]:py-4 [&>blockquote]:my-8
          [&>img]:rounded-none [&>img]:my-8 [&>img]:border-2 [&>img]:border-primary
          [&>pre]:bg-card [&>pre]:p-4 [&>pre]:rounded-none [&>pre]:border [&>pre]:border-border [&>pre]:overflow-x-auto [&>pre]:my-6
          [&>code]:bg-muted [&>code]:px-1.5 [&>code]:py-0.5 [&>code]:rounded-none [&>code]:text-sm"
        dangerouslySetInnerHTML={{ __html: post.content || '' }} 
      />

      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-12">
          <Tag className="size-5 text-primary mr-2" />
          {post.tags.map((tag, i) => (
            <Link key={tag.id} to={tag.link || "#"} data-index={i}>
              <span className="inline-flex items-center px-3 py-1 rounded-none bg-secondary text-secondary-foreground text-xs font-bold uppercase hover:bg-primary hover:text-primary-foreground transition-colors">
                {tag.name}
              </span>
            </Link>
          ))}
        </div>
      )}

      <Separator className="my-12 bg-border" />

      {post.author && (
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-card p-8 rounded-none border-l-8 border-primary shadow-lg">
          {post.author.avatar && post.author.avatar['96'] ? (
            <img 
              src={post.author.avatar['96']} 
              alt={post.author.name} 
              className="size-24 rounded-none border-2 border-primary shadow-md shrink-0" 
            />
          ) : (
            <div className="size-24 rounded-none bg-muted flex items-center justify-center shrink-0 border-2 border-primary shadow-md">
              <User className="size-10 text-muted-foreground" />
            </div>
          )}
          <div className="text-center sm:text-left flex-1">
            <h3 className="text-xl font-bold font-default text-foreground mb-2 uppercase tracking-tight">
              Reported by {post.author.name}
            </h3>
            <p className="text-muted-foreground mb-4 font-default">
              Official WT Birkalla media team member providing the latest updates from the pitch and the community.
            </p>
            {post.author.link && (
              <Link to={post.author.link}>
                <Button variant="outline" size="sm" className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground uppercase font-bold text-xs">
                  More from {post.author.name}
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient ? window.wvcClient.getCurrentWPQueryParams() : { post_type: 'post' };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="245265" id="single-post-template-section" className="bg-background text-foreground py-16 md:py-24 px-4 md:px-8 min-h-screen">
      <div className="container mx-auto">
        <PostsProvider wp_query={wp_query}>
          <SinglePostConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}